import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import Dropdown from "../ui/Dropdown";
import { formatDateTime } from "../../utils/dateUtils";

const EditPicksModal = ({ isOpen, onClose, user, onEditPicks }) => {
  const [selectedWeek, setSelectedWeek] = useState("");
  const [games, setGames] = useState([]);
  const [userPicks, setUserPicks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const weeks = Array.from({ length: 22 }, (_, i) => ({
    value: (i + 1).toString(),
    label: `Week ${i + 1}`,
  }));

  const fetchGamesAndPicks = useCallback(async () => {
    if (!user || !user.id) return;
    try {
      setLoading(true);
      setError("");

      // Fetch games for the week
      const gamesResponse = await axios.get(
        `${API_URL}/games/week/${selectedWeek}`,
      );
      const weekGames = gamesResponse.data.games || [];

      // Fetch user's picks for the week
      const picksResponse = await axios.get(
        `${API_URL}/picks/user/${user.id}/week/${selectedWeek}`,
      );
      const picks = picksResponse.data.picks || [];

      setGames(weekGames);
      setUserPicks(picks);
    } catch (err) {
      setError("Failed to fetch games and picks");
    } finally {
      setLoading(false);
    }
  }, [selectedWeek, user?.id]);

  useEffect(() => {
    fetchGamesAndPicks();
  }, [fetchGamesAndPicks]);

  const handlePickChange = (gameId, teamId) => {
    setUserPicks((prev) => {
      const existingPickIndex = prev.findIndex(
        (pick) => pick.gameId === gameId,
      );

      if (existingPickIndex >= 0) {
        // Update existing pick
        const updated = [...prev];
        updated[existingPickIndex] = { ...updated[existingPickIndex], teamId };
        return updated;
      } else {
        // Add new pick
        return [
          ...prev,
          { gameId, teamId, userId: user?.id, week: parseInt(selectedWeek) },
        ];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user || !user.id) return;
    try {
      setLoading(true);
      await onEditPicks(user.id, selectedWeek, userPicks);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error?.message || "Failed to update picks");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedWeek("");
    setGames([]);
    setUserPicks([]);
    setError("");
    onClose();
  };

  if (!user || !user.id) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Edit User Picks">
        <div className="p-6 text-center text-gray-500">Loading user...</div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit User Picks"
      size="lg"
    >
      <div className="space-y-4">
        <div>
          <p className="text-sm text-gray-600 mb-4">
            Edit picks for <strong>{user?.name}</strong>
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div>
          <Dropdown
            label="Select Week"
            value={selectedWeek}
            onChange={setSelectedWeek}
            options={weeks}
            placeholder="Choose a week..."
          />
        </div>

        {selectedWeek && (
          <div className="max-h-96 overflow-y-auto">
            <h4 className="font-medium mb-3">Week {selectedWeek} Games</h4>

            {loading ? (
              <div className="text-center py-4">Loading games...</div>
            ) : games.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                No games found for this week
              </div>
            ) : (
              <div className="space-y-3">
                {games.map((game) => {
                  const userPick = userPicks.find(
                    (pick) => pick.gameId === game.id,
                  );

                  return (
                    <div key={game.id} className="border rounded-lg p-4">
                      <div className="text-sm font-medium mb-2">
                        {game.awayTeam} @ {game.homeTeam}
                      </div>
                      <div className="text-xs text-gray-500 mb-3">
                        {formatDateTime(game.gameDate)}
                      </div>

                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            handlePickChange(game.id, game.awayTeam)
                          }
                          className={`flex-1 py-2 px-3 rounded text-sm font-medium border ${
                            userPick?.teamId === game.awayTeam
                              ? "bg-blue-500 text-white border-blue-500"
                              : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {game.awayTeam}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            handlePickChange(game.id, game.homeTeam)
                          }
                          className={`flex-1 py-2 px-3 rounded text-sm font-medium border ${
                            userPick?.teamId === game.homeTeam
                              ? "bg-blue-500 text-white border-blue-500"
                              : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {game.homeTeam}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !selectedWeek || games.length === 0}
          >
            {loading ? "Updating..." : "Update Picks"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditPicksModal;
